import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { fmtPrice } from "@/lib/constants";

// Imperative Leaflet map with custom price pins (React 19 safe).
const MapView = ({ villas = [], center = [36.51, -4.92], zoom = 12, height = 460 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      center,
      zoom,
      scrollWheelZoom: false,
      zoomControl: true,
    });
    mapRef.current = map;
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; OpenStreetMap &copy; CARTO',
      maxZoom: 19,
    }).addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const layer = L.layerGroup().addTo(map);
    const pts = [];
    villas.forEach((v) => {
      if (!v.location) return;
      const { lat, lng } = v.location;
      pts.push([lat, lng]);
      const pinText = v.label || fmtPrice(v.price_per_night);
      const wide = !!v.label;
      const icon = L.divIcon({
        className: "",
        html: `<div class="price-pin">${pinText}</div>`,
        iconSize: [wide ? 110 : 70, 28],
        iconAnchor: [wide ? 55 : 35, 14],
      });
      const marker = L.marker([lat, lng], { icon }).addTo(layer);
      if (v.label) {
        // Custom point (e.g. our office) — no price / booking link.
        marker.bindPopup(
          `<div style="width:210px;font-family:Manrope,sans-serif">
             <div style="font-weight:700;font-size:14px">${v.name}</div>
             ${v.address ? `<div style="color:#5E5A52;font-size:12px;margin-top:3px;line-height:1.4">${v.address}</div>` : ""}
           </div>`
        );
      } else {
        const img = v.images?.[0]?.url || "";
        marker.bindPopup(
          `<div style="width:200px;font-family:Manrope,sans-serif">
             <img src="${img}" style="width:100%;height:110px;object-fit:cover;border-radius:10px"/>
             <div style="font-weight:700;margin-top:6px;font-size:14px">${v.name}</div>
             <div style="color:#5E5A52;font-size:12px">${v.area} \u00b7 sleeps ${v.max_guests}</div>
             <div style="margin-top:4px;font-weight:700">${fmtPrice(v.price_per_night)} <span style="font-weight:400;color:#5E5A52">/ night</span></div>
             <a href="/villas/${v.slug}" style="display:inline-block;margin-top:8px;background:#141414;color:#fff;padding:6px 12px;border-radius:8px;text-decoration:none;font-size:12px">View villa</a>
           </div>`
        );
      }
    });
    if (pts.length > 1) {
      try { map.fitBounds(pts, { padding: [40, 40], maxZoom: 14 }); } catch (e) {}
    }
    return () => {
      layer.clearLayers();
      map.removeLayer(layer);
    };
  }, [villas]);

  return <div ref={containerRef} data-testid="villas-map" style={{ height, width: "100%" }} className="rounded-[18px] border border-border lux-shadow" />;
};

export default MapView;
